import { CandlestickData, MarkerType, SingleValueData, Time } from 'lightweight-charts'
import {
    barSeriesOptions,
    candleSeriesOptions,
    ChartViewKey,
    histogramSeriesOptions,
    lineSeriesOptions
} from '../assets/constants'
import { TimeBucket, TimeBucket_TimeSeries, timeBucket_TimeSeriesToJSON } from '../proto/generated/common'
import { GetCandlesBulkResponseV2, GetCandlesRequestV2 } from '../proto/generated/candle-data-depot'
import { SuperChartConfiguration_TimeBucket } from '../proto/generated/superchart-page'
import { IndicatorParams, SeriesGroup, SeriesGroupCategory } from '../pages/home'
import { IndicatorUtils } from './IndicatorUtils/indicatorUtils'
import { PaneData } from '../pages/chartPage/ChartPage'
import { ChartType } from '../proto/generated/chart'

import { v4 as uuid } from 'uuid'
import { TimeZone, toDayStart, utcToIST } from './dateTime'
import { TradingCalendarEvent } from '../proto/generated/trading-calendar'
import { candleUpColorAlpha, lightRed } from '../assets/colors'
import { PaneMetaInfo } from '../reducer/common.reducer'
import { CandleTypes } from '../components/chartV2/chart.enums'
import { ChartCandle, ChartSeries, SeriesData, SeriesOptions } from '../components/chartV2/chart.typings'
import { LineBreakChart } from './specialChartTypes/LineBreakChart'
import { KagiChart } from './specialChartTypes/KagiChart'
import { IndicatorOutput_OutputStyle } from '../proto/generated/indicator'
import { getLineStyleFromOutput } from './chart'
import { RenkoChart } from './specialChartTypes/RenkoChart'
import { PandFChart } from './specialChartTypes/PandFChart'
import { Candle } from '../proto/generated/candle-data-depot-common'

export const filterDuplicateCandles = (candles: any[]): any[] => {
    const newCandles: any[] = []
    candles.forEach((candle, index) => {
        if (index + 1 < candles.length && (candle.startTimestamp || candle.time || candle.timestamp) >= (candles[index + 1].startTimestamp || candles[index + 1].time || candles[index + 1].timestamp)) return
        newCandles.push(candle)
    })
    return newCandles
}

export const calculateTimeBucket = (date1: Date | undefined, date2: Date | undefined, holidayCnt?: number): TimeBucket => {
    if (!date1 || !date2) {
        return {
            timeSeries: TimeBucket_TimeSeries.MINUTE,
            value: 1
        }
    }
    const d1 = new Date(date1)
    const d2 = new Date(date2)
    let daysDifference = 0
    while (d1.getFullYear() <= d2.getFullYear() && d1.getMonth() <= d2.getMonth() && d1.getDate() <= d2.getDate()) {
        daysDifference++
        d1.setDate(d1.getDate() + 1)
    }

    if (holidayCnt) {
        daysDifference -= holidayCnt
    }
    if (daysDifference > 30) {
        return {
            timeSeries: TimeBucket_TimeSeries.DAY,
            value: 1
        }
    }
    if (daysDifference > 1) {
        return {
            timeSeries: TimeBucket_TimeSeries.HOUR,
            value: 1
        }
    }
    return {
        timeSeries: TimeBucket_TimeSeries.MINUTE,
        value: 1
    }
}

export const calculateHolidaysBetweenDates = (date1: Date | undefined, date2: Date | undefined, calenderEvents: TradingCalendarEvent[]): number => {
    if (!date1 || !date2) {
        return 0
    }
    const d1 = new Date(date1)
    const d2 = new Date(date2)
    const holidays = new Set<string>(calenderEvents.map((event) => event.date))
    let cnt = 0
    while (d1.getFullYear() <= d2.getFullYear() && d1.getMonth() <= d2.getMonth() && d1.getDate() <= d2.getDate()) {
        const date = `${d1.getFullYear().toString().padStart(4, '0')}${(d1.getMonth() + 1).toString().padStart(2, '0')}${d1.getDate().toString().padStart(2, '0')}`
        if (holidays.has(date)) {
            cnt++
        }
        d1.setDate(d1.getDate() + 1)
    }
    return cnt
}

export const formatDateRange = (date1: Date, date2: Date): [Date, Date] => {
    date1.setHours(9, 15, 0)
    const currentDate: Date = new Date()
    if (currentDate.getFullYear() === date2.getFullYear() && currentDate.getMonth() === date2.getMonth() && currentDate.getDate() === date2.getDate()) {
        if (currentDate.getHours() > 15 && currentDate.getMinutes() > 30) {
            currentDate.setHours(15, 30, 0)
        }
        return [date1, currentDate]
    } else {
        date2.setHours(15, 30, 0)
        return [date1, date2]
    }
}

export const calculateCandlesBetweenDates = (range: [Date, Date], timeBucket: TimeBucket): number => {
    const startTime = range[0].getTime()
    const endTime = range[1].getTime()
    const actualTimeDifference = Math.max(endTime - startTime, 0)
    
    const startDay = toDayStart(startTime)
    const endDay = toDayStart(endTime)
    const daysDifference = Math.floor((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000))
    const totalCalendarDays = daysDifference + 1
    
    if ([TimeBucket_TimeSeries.MINUTE, TimeBucket_TimeSeries.HOUR, TimeBucket_TimeSeries.SECOND].includes(timeBucket.timeSeries)) {
        if (daysDifference === 0) {
            if (timeBucket.timeSeries === TimeBucket_TimeSeries.SECOND) {
                return Math.ceil(actualTimeDifference / (timeBucket.value * 1000))
            }
            const timeWidthInMilliseconds = timeBucket.value * 60 * 1000
            return Math.ceil(actualTimeDifference / timeWidthInMilliseconds)
        }
        const candlesPerDay = dayCandleCount[timeBucket.timeSeries] / timeBucket.value
        return Math.ceil(totalCalendarDays * candlesPerDay * 1.5)
    }
    
    if (daysDifference === 0) {
        return 1
    }
    return Math.ceil(totalCalendarDays * dayCandleCount[timeBucket.timeSeries] / timeBucket.value * 1.5)
}


export const convertSecurityCandles = (candles: Candle[], priceMultiplier: number): any[] => {
    return candles.map((candle) => {
        return {
            time: candle.startTimestamp / 1000,
            open: candle.open / priceMultiplier,
            high: candle.high / priceMultiplier,
            low: candle.low / priceMultiplier,
            close: candle.close / priceMultiplier,
            customValues: {
                vwap: candle.vwap / priceMultiplier,
                volume: candle.volume
            } as Record<string, unknown>,
            referencedSecurityCode: candle.referencedSecurityCode
        } as any
    })
}

export const extractCandleValue = (candle: CandlestickData, key: string) => {
    switch (key) {
        case 'close':
            return candle.close
        case 'open':
            return candle.open
        case 'high':
            return candle.high
        case 'low':
            return candle.low
        case 'volume':
            return candle.customValues!['volume'] as number
        default:
            return candle.close
    }
}

export const createFreshPaneData = () => {
    return {
        id: uuid(),
        grps: [] as SeriesGroup[],
        primary: false
    }
}

export type AppendParams = {
    series: ChartSeries,
    newData: ChartCandle[],
    category: SeriesGroupCategory,
    timezone: TimeZone,
    chartType?: ChartType
}

export type NonPrimarySeriesGroupCategory = SeriesGroupCategory.SERIES_INDICATOR | SeriesGroupCategory.SERIES_VOLUME

export const appendRawData = (
    oldData: any[],
    newData: any[]
): any[] => {
    if (oldData && oldData.length < 1) {
        oldData.push(...newData)
    } else if (oldData) {
        let lastPreviousCandleIndex = oldData.length - 1
        if (oldData[lastPreviousCandleIndex].time < newData[0].time) {
            oldData.push(...newData)
        } else if (oldData[lastPreviousCandleIndex].time === newData[0].time) {
            oldData.splice(lastPreviousCandleIndex, 1)
            oldData.push(...newData)
        }
    }
    return oldData
}

export enum IndicatorDataRequestType {
    BACK_SCROLL,
    CURRENT,
    LIVE_UPDATE
}

export type IndicatorDataParams = {
    previousCandles: ChartCandle[]
    candles: ChartCandle[]
    seriesGrp: SeriesGroup
    script: string
    ta: IndicatorUtils
    params: Partial<IndicatorParams>
    timezone: TimeZone
    requestType: IndicatorDataRequestType
    paneIndex?: number
    candleType: CandleTypes,
    viewKey: ChartViewKey,
    panesMetaInfo: PaneMetaInfo[]
}

export const fetchBulkTickDataV2 = async (
    fetchBulkTrigger: any,
    candlesCount: number,
    timeBucket: SuperChartConfiguration_TimeBucket,
    requestData: GetCandlesRequestV2[]
): Promise<GetCandlesBulkResponseV2 | null> => {
    if (requestData.length === 0) return null
    const body = Object.assign(
        {},
        {
            requests: requestData.map((r) => {
                return {
                    time_bucket: {
                        time_series: timeBucket_TimeSeriesToJSON(timeBucket.timeSeries),
                        value: timeBucket.timeValue
                    },
                    security: r.security,
                    number_of_candles: candlesCount,
                    dont_give_extra_candles: r.dontGiveExtraCandles ? r.dontGiveExtraCandles : false
                }
            })
        }
    )

    try {
        const response = await fetchBulkTrigger({
            body: body
        })
        return response
    } catch (error) {
        console.error('error', error)
        return null
    }
}


export function convertCandles({ candles, chartType, timeZone, ignorePrevData, convertTimes = true }: {
    candles: (CandlestickData | SingleValueData)[],
    chartType: ChartType,
    timeZone: TimeZone,
    ignorePrevData?: boolean,
    convertTimes?: boolean
}): SeriesData[] {
    if (!candles.length) {
        return candles
    }
    // let prevData: SingleValueData | undefined = undefined
    candles = filterDuplicateCandles((convertTimes ? convertTime(candles, timeZone) : candles).sort((a, b) => (a.time as number - (b.time as number))))
    if ('value' in candles[0]) {
        return candles
    }
    switch (chartType) {
        case ChartType.BAR:
        case ChartType.CANDLESTICK:
            return candles
        case ChartType.LINE_BREAK:
            const linebreak = new LineBreakChart(candles as CandlestickData[], 3)
            return linebreak.getCandles()
        case ChartType.KAGI:
            const kagi = new KagiChart({ candles: candles as CandlestickData[] })
            return kagi.getCandles()
        case ChartType.RENKO:
            const renko = new RenkoChart({ candles: candles as CandlestickData[] })
            return renko.getCandles()
        case ChartType.POINT_AND_FIGURE:
            const pAndF = new PandFChart({ candles: candles as CandlestickData[] })
            return pAndF.getCandles()
        case ChartType.RANGE:
            const range = new KagiChart({ candles: candles as CandlestickData[] })
            return range.getCandles()
        case ChartType.HEIKIN_ASHI:
            const heikinAshiCandles: CandlestickData[] = []
            if (!ignorePrevData) {
                heikinAshiCandles.push({
                    ...candles[0],
                    time: candles[0].time,
                    close: NaN,
                    open: NaN,
                    high: NaN,
                    low: NaN
                })
            }
            for (let i = 1; i < candles.length; i++) {
                const candle = candles[i]
                const prevCandle = (i > 1) ? heikinAshiCandles[heikinAshiCandles.length - 1] : candles[i - 1]
                const candleHigh = (candle as CandlestickData).high
                const candleLow = (candle as CandlestickData).low
                const candleOpen = (candle as CandlestickData).open
                const candleClose = (candle as CandlestickData).close
                const prevCandleOpen = (prevCandle as CandlestickData).open
                const prevCandleClose = (prevCandle as CandlestickData).close

                const heikinClose = (candleOpen + candleClose + candleHigh + candleLow) / 4
                const heikinOPen = (prevCandleOpen + prevCandleClose) / 2
                const heikinHigh = Math.max(candleHigh, Math.max(heikinClose, heikinOPen))
                const heikinLow = Math.min(candleLow, Math.min(heikinClose, heikinOPen))
                heikinAshiCandles.push({
                    ...candle,
                    time: candle.time,
                    close: heikinClose,
                    open: heikinOPen,
                    high: heikinHigh,
                    low: heikinLow
                })
            }
            return heikinAshiCandles
        case ChartType.AREA:
        case ChartType.LINE:
            return candles.map((candle) => {
                return { value: (candle as CandlestickData).close, time: candle.time }
            })
        case ChartType.HISTOGRAM:
            return candles.map((candle) => {
                const color = (candle as CandlestickData).close >= (candle as CandlestickData).open ? candleUpColorAlpha : lightRed
                return { value: (candle as CandlestickData).close, time: candle.time, color }
            })
        default:
            return candles
    }
}

export function convertTime(data: (CandlestickData | SingleValueData)[], timezone: TimeZone): (CandlestickData | SingleValueData)[] {
    if (!data.length) return []
    switch (timezone) {
        case TimeZone.IST:
            return data.map((candle) => {
                return {
                    ...candle,
                    time: utcToIST(candle.time as number) as Time
                }
            })
        case TimeZone.UTC:
            return data
        default:
            return data
    }
}

export function zonedTime(time: number, timezone: TimeZone): number {
    switch (timezone) {
        case TimeZone.IST:
            return utcToIST(time as number)
        case TimeZone.UTC:
            return time
        default:
            return time
    }
}

export const getPanesDataCopy = (panesData: PaneData[]): PaneData[] => {
    const newPanesData = JSON.parse(JSON.stringify(panesData)) as PaneData[]
    newPanesData.forEach((paneData) => {
        paneData.grps.sort((a, b) => a.addedTime - b.addedTime)
    })
    return newPanesData
}

export function getSeriesOptions(chartType: ChartType, commonOptions: SeriesOptions): SeriesOptions {
    switch (chartType) {
        case ChartType.BAR:
            return { ...barSeriesOptions, ...commonOptions }
        case ChartType.CANDLESTICK:
            return { ...candleSeriesOptions, ...commonOptions }
        case ChartType.AREA:
            return { ...commonOptions }
        case ChartType.HISTOGRAM:
            return { ...histogramSeriesOptions, ...commonOptions }
        case ChartType.LINE:
            return { ...lineSeriesOptions, ...commonOptions }
        default:
            return { ...candleSeriesOptions, ...commonOptions }
    }
}

export const dayCandleCount: Record<TimeBucket_TimeSeries, number> = {
    [TimeBucket_TimeSeries.DAY]: 1,
    [TimeBucket_TimeSeries.MINUTE]: 375,
    [TimeBucket_TimeSeries.HOUR]: 7,
    [TimeBucket_TimeSeries.SECOND]: 2250,
    // TODO when supported
    [TimeBucket_TimeSeries.TIME_SERIES_UNKNOWN]: 0,
    [TimeBucket_TimeSeries.WEEK]: 1 / 5,
    [TimeBucket_TimeSeries.MONTH]: 1 / 21,
    [TimeBucket_TimeSeries.YEAR]: 1 / 252,
    [TimeBucket_TimeSeries.UNRECOGNIZED]: 0
}

export const convertTimeStampTodate = (timestamp: any) => {
    const milliseconds = timestamp * 1000
    const date = new Date(milliseconds)
    const options = {
        timeZone: 'Asia/Kolkata',
        day: '2-digit' as const,
        month: 'short' as const,
        year: '2-digit' as const
    }
    const formattedDate = date.toLocaleDateString('en-US', options).split('-')
    return `${formattedDate[0]} ${formattedDate[1]} '${formattedDate[2]}`
}

export const getSeriesOptionsFromIndicatorOutputStyle = (style: IndicatorOutput_OutputStyle | undefined) => {
    switch (style) {
        case IndicatorOutput_OutputStyle.WITH_DIAMONDS:
            return {
                markerType: MarkerType.Diamond
            }
        case IndicatorOutput_OutputStyle.WITH_BREAKS:
            return {
                withBreaks: true
            }
        default:
            return {
                lineStyle: getLineStyleFromOutput(style || IndicatorOutput_OutputStyle.SOLID)
            }
    }
}